/**
 * Copilot API service
 *
 * Handles communication with the copilot backend endpoints:
 * - POST /api/copilot/transcribe — audio blob → transcript
 * - POST /api/copilot/trip-reports/extract — transcript → TripReportDraft
 */

import api from '../../services/api';

// ---------------------------------------------------------------------------
// Types — mirror backend Pydantic models from app/routes/copilot.py
// ---------------------------------------------------------------------------

export interface TranscribeResponse {
  transcript: string;
  duration_seconds: number;
  confidence: number;
}

export interface ExtractRequest {
  transcript: string;
  model?: string;
}

export interface Attendee {
  name: string;
  role: string | null;
  is_new_contact: boolean;
}

export interface Topic {
  subject: string;
  detail: string;
  sentiment: 'positive' | 'negative' | 'neutral' | 'mixed';
  products_mentioned: string[];
}

export interface ActionItem {
  task: string;
  owner: string | null;
  deadline_mentioned: string | null;
  priority: 'high' | 'medium' | 'low';
}

export interface ContactUpdate {
  name: string;
  new_info: string;
}

export interface TripReportDraft {
  account_name: string | null;
  other_companies_mentioned: string[];
  account_uuid: string | null;
  account_resolution_label: string | null;
  visit_date: string | null;
  visit_type: 'in_person' | 'phone_call' | 'email' | 'other';
  salesperson_name: string | null;
  attendees: Attendee[];
  topics: Topic[];
  action_items: ActionItem[];
  competitors_mentioned: string[];
  contact_updates: ContactUpdate[];
  overall_sentiment: 'positive' | 'negative' | 'neutral' | 'mixed';
  confidence: number;
  raw_transcript: string;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * Upload an audio blob for transcription via Whisper.
 * Uses multipart/form-data — the backend expects an UploadFile named "file".
 */
export async function transcribeAudio(
  blob: Blob,
  mimeType: string,
): Promise<TranscribeResponse> {
  const ext = mimeType.includes('mp4') ? '.mp4'
    : mimeType.includes('webm') ? '.webm'
    : mimeType.includes('ogg') ? '.ogg'
    : '.webm';

  const formData = new FormData();
  formData.append('file', blob, `recording${ext}`);

  const response = await api.post<TranscribeResponse>(
    '/copilot/transcribe',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180_000, // up to 3 minutes: covers retries on server side
    },
  );
  return response.data;
}

/**
 * Extract structured trip report data from transcript text.
 */
export async function extractTripReport(
  transcript: string,
): Promise<TripReportDraft> {
  const body: ExtractRequest = { transcript };
  const response = await api.post<TripReportDraft>(
    '/copilot/trip-reports/extract',
    body,
    { timeout: 180_000 }, // up to 3 minutes: covers retries on server side
  );
  return response.data;
}

/**
 * Convert text to speech via OpenAI TTS API.
 * Returns an audio Blob (audio/mpeg) for playback.
 *
 * `model` is optional — omit it to use the backend default (`tts-1`, low
 * latency). Callers that want higher-quality audio pass a tier the endpoint
 * allowlists (`tts-1-hd`, `gpt-4o-mini-tts`); the Quality Co-Pilot uses
 * `gpt-4o-mini-tts`.
 */
export async function synthesizeSpeech(
  text: string,
  voice: string = 'nova',
  model?: string,
  provider?: string,
): Promise<Blob> {
  const body: { text: string; voice: string; model?: string; provider?: string } = {
    text,
    voice,
  };
  if (model) body.model = model;
  if (provider) body.provider = provider;
  const response = await api.post(
    '/copilot/tts',
    body,
    {
      responseType: 'blob',
      timeout: 30_000,
    },
  );
  return response.data as Blob;
}
